import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@todotogether/backend/_generated/api";
import type { Id } from "@todotogether/backend/_generated/dataModel";
import {
  type BeatState,
  type ListTypingPresence,
  PRESENCE_TICK_MS,
  beatDue,
  composerTypists,
  freeTeaseLive,
  isPresenceLive,
  liveTypists,
} from "@todotogether/shared";

/**
 * The typing broadcaster, web twin of mobile's (#386 — a pure port of
 * the #385 vocabulary): every real keystroke calls noteTyping; the
 * shared beatDue decides whether it owes the server a beat. Web pages
 * unmount with the route, so the unmount cleanup IS the screen-exit
 * teardown — no focus gate to hold. NOT journaled (#384: presence
 * dies with connectivity, by design); refusals are swallowed — an
 * ephemeral decoration owes nobody an error banner.
 */
export function useTypingBroadcaster(listId: Id<"lists">) {
  const beat = useMutation(api.presence.beat);
  const clear = useMutation(api.presence.clear);
  const state = useRef<BeatState>({ surface: null, lastBeatAt: null });
  const mutations = useRef({ beat, clear });
  mutations.current = { beat, clear };

  const stopTyping = () => {
    if (state.current.surface === null) return;
    state.current = { surface: null, lastBeatAt: null };
    void mutations.current.clear({ listId }).catch(() => undefined);
  };

  const noteTyping = (surface: string, itemId?: Id<"items">) => {
    const now = Date.now();
    if (!beatDue(state.current, surface, now)) return;
    state.current = { surface, lastBeatAt: now };
    void mutations.current.beat({ listId, itemId }).catch(() => undefined);
  };

  const stopTypingRef = useRef(stopTyping);
  stopTypingRef.current = stopTyping;
  useEffect(() => () => stopTypingRef.current(), []);

  return { noteTyping, stopTyping };
}

/**
 * The reader, web twin of mobile's: the gated List read, the fade
 * clock (the wire keeps stale rows — #384's timing law — so liveness
 * re-evaluates on the shared tick while anything is still LIVE; stale
 * rows ride the wire forever, and keying the tick on "data exists"
 * would run it forever after one burst), and the derived render state.
 * Which arm renders derives from the payload's OWN shape, never a
 * local tier read — the mirrored-gate law.
 */
export function useListTypingPresence(
  listId: string | undefined,
): ListTypingPresence {
  const presence = useQuery(
    api.presence.forList,
    listId ? { listId: listId as Id<"lists"> } : "skip",
  );
  const [now, setNow] = useState(() => Date.now());
  const anythingLive =
    presence !== null &&
    presence !== undefined &&
    ("typists" in presence
      ? presence.typists.some((row) => isPresenceLive(row.updatedAt, now))
      : presence.latestActivityAt !== null &&
        isPresenceLive(presence.latestActivityAt, now));
  useEffect(() => {
    setNow(Date.now());
  }, [presence]);
  useEffect(() => {
    if (!anythingLive) return;
    const id = setInterval(() => setNow(Date.now()), PRESENCE_TICK_MS);
    return () => clearInterval(id);
  }, [anythingLive]);
  const typists = liveTypists(presence, now);
  return {
    typists,
    adders: composerTypists(typists),
    teaseLive: freeTeaseLive(presence, now),
  };
}
